import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BankProvider } from '@/types/bankIntegration';

export interface BankTransaction {
  external_id: string;
  date: string;
  description: string;
  value: number;
  type: 'credit' | 'debit';
  balance_after?: number;
  category?: string;
  counterpart_name?: string;
  counterpart_document?: string;
}

interface SyncRequest {
  financial_account_id: string;
  provider: BankProvider;
  start_date: string;
  end_date: string;
}

interface SyncResponse {
  success: boolean;
  transactions: BankTransaction[];
  account_name: string;
  synced_at: string;
  error?: string;
}

export const useSyncBankTransactions = () => {
  const { toast } = useToast();

  const syncMutation = useMutation({
    mutationFn: async (request: SyncRequest): Promise<SyncResponse> => {
      const { data, error } = await supabase.functions.invoke('sync-bank-transactions', {
        body: request,
      });

      if (error) {
        throw new Error(error.message || 'Erro ao sincronizar extrato');
      }

      if (!data.success) {
        throw new Error(data.error || 'Falha na sincronização');
      }

      return data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Extrato sincronizado!',
        description: `${data.transactions.length} transações encontradas`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro na sincronização',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    syncTransactions: syncMutation.mutateAsync,
    isSyncing: syncMutation.isPending,
    lastSync: syncMutation.data,
    error: syncMutation.error,
  };
};